/**
 * Third-party meeting links a VRS interpreter may be sent to ("Interpret my
 * meeting"; 47 CFR 64.604(e), integrated VRS in video conferences). One copy
 * for the server and both clients: vrs-server requires it, the web
 * client-profile.js reads it as window.MalkaExternalMeetingLinks, native
 * imports it.
 *
 * The allowlist is the security boundary, not a convenience: an interpreter
 * opens this link on their own machine, so the server must only ever hand
 * them a meeting on a known platform. The server derives the platform from
 * the host here and never trusts one sent by the client.
 *
 * Parsed with a regular expression rather than URL, which React Native does
 * not implement completely. Anything unusual (credentials, a port, a bare
 * host with no meeting path) is refused rather than guessed at.
 */
(function(root, factory) {
    const externalMeetingLinks = factory();
    if (typeof module === 'object' && module.exports) module.exports = externalMeetingLinks;
    else root.MalkaExternalMeetingLinks = externalMeetingLinks;
})(typeof self !== 'undefined' ? self : this, function() {
    const MAX_LINK_LENGTH = 1000;

    const PLATFORM_LABELS = Object.freeze({
        meet: 'Google Meet',
        teams: 'Microsoft Teams',
        webex: 'Webex',
        zoom: 'Zoom'
    });

    // Apex domains per platform; a host matches the apex itself or any
    // subdomain of it (company.zoom.us), never a lookalike (evilzoom.us).
    const PLATFORM_DOMAINS = Object.freeze({
        meet: [ 'meet.google.com' ],
        teams: [ 'teams.microsoft.com', 'teams.live.com', 'teams.microsoft.us' ],
        webex: [ 'webex.com', 'webexgov.us' ],
        zoom: [ 'zoom.us', 'zoomgov.com' ]
    });

    const LINK_PATTERN = /^https:\/\/([^/?#]+)(\/[^?#]*)?(\?[^#]*)?(#.*)?$/i;
    const HOST_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

    function refuse(code, message) {
        return { code, message, ok: false };
    }

    function hostMatches(host, apex) {
        return host === apex || host.endsWith(`.${apex}`);
    }

    function platformForHost(host) {
        for (const platform of Object.keys(PLATFORM_DOMAINS)) {
            if (PLATFORM_DOMAINS[platform].some(apex => hostMatches(host, apex))) {
                return platform;
            }
        }

        return null;
    }

    function queryParam(query, name) {
        const match = new RegExp(`[?&]${name}=([^&]*)`, 'i').exec(query || '');
        if (!match) return null;
        try {
            return decodeURIComponent(match[1].replace(/\+/g, ' '));
        } catch {
            return match[1];
        }
    }

    function decodeSegment(segment) {
        try {
            return decodeURIComponent(segment);
        } catch {
            return segment;
        }
    }

    // A link that only names the platform (zoom.us/, meet.google.com/) gives
    // the interpreter nothing to join. Each platform must show a meeting path;
    // the returned id is evidence for the call record, best effort.
    function meetingIdFor(platform, path, query) {
        const segments = path.split('/').filter(Boolean);

        if (platform === 'zoom') {
            const match = /^\/(?:j|w|s|wc(?:\/join)?)\/(\d{9,12})(?:\/|$)/.exec(path)
                || /^\/wc\/(\d{9,12})\/join(?:\/|$)/.exec(path);
            if (match) return match[1];
            const vanity = /^\/my\/([A-Za-z0-9._-]{1,64})\/?$/.exec(path);
            return vanity ? `my/${vanity[1]}` : null;
        }

        if (platform === 'meet') {
            const match = /^\/([a-z]{3}-[a-z]{4}-[a-z]{3})\/?$/.exec(path);
            return match ? match[1] : null;
        }

        if (platform === 'teams') {
            if (segments[0] === 'l' && segments[1] === 'meetup-join' && segments[2]) {
                return decodeSegment(segments[2]).slice(0, 255);
            }
            if (segments[0] === 'meet' && /^[A-Za-z0-9]{6,64}$/.test(segments[1] || '')) {
                return segments[1];
            }
            return null;
        }

        if (platform === 'webex') {
            const mtid = queryParam(query, 'MTID');
            if (mtid && /^[A-Za-z0-9]{8,128}$/.test(mtid)) return mtid;
            const personal = segments[0] === 'meet' ? segments[1] : segments[1] === 'meet' ? segments[2] : null;
            if (personal && /^[A-Za-z0-9._-]{1,64}$/.test(personal)) return `meet/${personal}`;
            if (segments[0] === 'wbxmjs' || segments[0] === 'join') return segments[segments.length - 1] || null;
            return null;
        }

        return null;
    }

    /**
     * @returns {{ok: true, url: string, platform: string, platformLabel: string, host: string, meetingId: string}
     *   | {ok: false, code: string, message: string}}
     */
    function parseMeetingLink(input) {
        if (typeof input !== 'string') {
            return refuse('EXTERNAL_MEETING_LINK_REQUIRED', 'Paste the link to your meeting.');
        }

        const url = input.trim();
        if (!url) {
            return refuse('EXTERNAL_MEETING_LINK_REQUIRED', 'Paste the link to your meeting.');
        }
        if (url.length > MAX_LINK_LENGTH || /[\s\u0000-\u001f\u007f]/.test(url)) {
            return refuse('EXTERNAL_MEETING_LINK_INVALID', 'That doesn’t look like a meeting link.');
        }

        const match = LINK_PATTERN.exec(url);
        if (!match) {
            return refuse('EXTERNAL_MEETING_LINK_INVALID', 'Meeting links start with https://');
        }

        const authority = match[1];
        if (authority.includes('@') || authority.includes(':')) {
            return refuse('EXTERNAL_MEETING_LINK_INVALID', 'That doesn’t look like a meeting link.');
        }

        const host = authority.toLowerCase();
        if (!HOST_PATTERN.test(host)) {
            return refuse('EXTERNAL_MEETING_LINK_INVALID', 'That doesn’t look like a meeting link.');
        }

        const platform = platformForHost(host);
        if (!platform) {
            return refuse('EXTERNAL_MEETING_LINK_UNSUPPORTED',
                'Interpreters can join Zoom, Google Meet, Microsoft Teams and Webex meetings.');
        }

        const meetingId = meetingIdFor(platform, match[2] || '/', match[3] || '');
        if (!meetingId) {
            return refuse('EXTERNAL_MEETING_LINK_INCOMPLETE',
                `Paste the full ${PLATFORM_LABELS[platform]} link for the meeting, not the ${PLATFORM_LABELS[platform]} home page.`);
        }

        return {
            host,
            meetingId,
            ok: true,
            platform,
            platformLabel: PLATFORM_LABELS[platform],
            url
        };
    }

    function platformLabel(platform) {
        return PLATFORM_LABELS[platform] || 'meeting';
    }

    return {
        MAX_LINK_LENGTH,
        PLATFORMS: Object.freeze(Object.keys(PLATFORM_LABELS)),
        PLATFORM_LABELS,
        parseMeetingLink,
        platformLabel
    };
});
